import {VNode} from 'snabbdom/vnode';

import {SortBy, SortOrder} from 'common/charts/visualization/bar-viz/types';

export const SEGMENT_PREFIX = `$mp-segment-`;

export interface DataTableAttrs {
  loading?: boolean;
  'multi-select'?: boolean;
  'selection-limit'?: number;
  collapsible?: boolean;
  'default-sort-field'?: string;
  'default-sort-order'?: number;
  'default-sorting'?: TableSortConfig;
  'has-comparison'?: boolean;
  'max-body-height': string;
  'num-sticky-rows'?: number;
  // TODO: remove sticky-columns-count
  'sticky-columns-count'?: number;
  'track-name': string;
  theme: Theme;
  'segmentation-data-table': boolean;
  'column-width-overrides'?: ColumnWidthOverrides;
}

/**
 * High level theme for table styles.
 *
 * hides the actual config options for easier development until they stabilize.
 */
export enum Theme {
  Funnels = `funnels`,
  Retention = `retention`,
  TrendData = `trend_data`,
}

/**
 * Low level table style options. Should be considered package private until they stabilize.
 *
 * If you need a change for a new context, add a Theme instead.
 */
export type ThemeSpec = {
  cellBorders: `line` | `gap`;
  cellBorderHover: `none` | `outline`;
  /** whether data cells in the table have are clickable and focusable */
  cellFocusable: boolean;
  cellHeaderBorders: `cell` | `column-group` | `none`;
  cellHeaderShadow: boolean;
  cellBackground: `focus-on-hover` | `heatmap` | `none`;
  cellShape: `round` | `square`;
  rowGroups: boolean;
  /** Explicit height in pixels for rows after the header row. This is required because without explicit row heights
   * the horizontal scrollbar (if one exists) will overlap the last row. */
  rowHeight: number;
};

/** Column width overrides (from resizing) in pixels */
export type ColumnWidthOverrides = {
  [columnIdx: number]: number;
};

export type ColumnResizeEvent = CustomEvent<{
  columnWidthOverrides: ColumnWidthOverrides;
}>;

export interface TableState {
  /** Table's rows to display, could be any array of objects */
  rows: Array<TableRow<TableCell>>;
  /** Number of rows displayed. Like items-menu, mp-table will progressively load more rows when user scrolls */
  numRowsToDisplay: number;
  /** Field that is currently sorted */
  sortField: string;
  /** Sort order, ascending = 1, descending = -1 */
  sortOrder: number;
  /** Sort config (replaces 'default-sort-field' and 'default-sort-order' to support multi-sort) */
  sorting: TableSortConfig;
  /** Whether the user has scrolled the body, so we can set the scroll shadow for titles and make it look sticky */
  hasBodyScrolled: boolean;
  cellToolTip: CellTooltip;
  columns: TableColumn[];
  columnWidthOverrides: ColumnWidthOverrides;
  resizeColumnIndex: number;
  /** The table's width in pixels. Necessary because div.scrollableColumns needs an explicit width
   * for horizontal scroll to work */
  tableWidth: number;
  // The context for the row that is being hovered over.
  hoveredContext: CellRenderContext;
}

/** (internal) Represents a stacked column header for the UI to render. */
export interface ColumnGroupHeader extends ColumnGroup {
  /** a map of css key to styles. contains grid formatting. */
  style?: any;
  /** accumulated width of the columns in this header */
  width: number;
  leftPosition: number;
  reverseColumnIndex: number;
  label?: string;

  /** renderer for the title */
  cellRenderer?(columnGroup: ColumnGroupHeader): VNode;
}

/** A grouping of TableColumns */
export interface ColumnGroup<Column = TableColumn> {
  /** Columns in this group */
  columns: Column[];
}

type SortTransformOptionalProps = {
  sortOrder: number;
};

export type SortTransformFunc<Row = any> = (
  row: Row,
  field: string,
  props?: SortTransformOptionalProps,
) => number | Date;

export type CellRendererFunc<Row = any> = (arg: {item: Row; context: CellRenderContext}) => VNode | string;

// TODO(j) some of these are never used, appears they were copied from mp-table. grep for unused ones in this directory and remove.
export interface TableColumn<Row = any> {
  /** The property inside each item that corresponds to the header */
  field: string;
  /** Title for the column header */
  title?: string;
  /** icon to show in place of a title */
  icon?: string;
  /** Type of field, affects the cell font.
   *
   * text (default) - no special styling.
   * number - mono font on data cells.
   * checkbox - (internal) special styling for the selection checkbox.
   */
  type?: `text` | `number` | `checkbox` | `collapsible`;
  /** width, in pixels, of the column */
  width: number;
  /** true if the column is not resizable (for e.g. checkbox column) */
  fixedWidth?: boolean;
  /** Resize minWidth in pixels. A default is used if this is omitted */
  minWidth?: number;
  /** whether this column should be pinned to the left */
  sticky?: boolean;
  /** text color for data cells. lower in priority than cells[] */
  textColor?: string;
  /** background color for data cells. lower in priority than cells[] */
  backgroundColor?: string;

  /** (internal) whether this column is the last of its column group and should have border */
  last?: boolean;
  /** (internal) whether this column is the first of its column group.  */
  first?: boolean;
  /** (internal) true if this is the "last last" column (last column in the last column group) */
  rightmost?: boolean;
  /** (internal) whether this column is the last sticky column */
  lastSticky?: boolean;

  /** whether this columm should have italics title */
  italics?: boolean;
  /** tooltip to give more info on column title, no (i) */
  tooltip?: string;
  /** tooltip alternative to the helptext */
  label?: string;
  /** placement of tooltip. default is bottom. */
  labelPlacement?: string;
  /** Help text shown when user hovers over (i) */
  helpText?: string;
  /** Placement of help text shown when user hovers over (i), e.g top right bottom left */
  helpTextPlacement?: string;
  /** Help text link (i) */
  helpLink?: string;
  /** if the cells can be clicked, hovered or focused */
  isSelectable?: boolean;
  /** If the field can be grouped */
  isGroupable?: boolean;
  /** If the field can be sorted */
  isSortable?: boolean;
  /** Only for text fields, search-filter will be matched against the field */
  isSearchable?: boolean;
  /** Maximum length of the title or column header, anything that exceeds the length will be middle truncated */
  titleMaxLength?: number;
  /** A custom renderer function (usally $mixins[`cell-{field}`]) */
  cellRenderer?: CellRendererFunc<Row>;
  /** A custom function that returns a number for sort comparison */
  sortTransform?: SortTransformFunc<Row>;
  /** A custom function that returns a label for display e.g 1234 -> 1,234 */
  labelTransform?: (row: Row, field: string) => string | boolean | number;
}

export type Columns<T> = Array<TableColumn | ColumnGroup<T>>;

export interface TableCell {
  /** numeric value that this cell contains */
  value: number;
  /** the display string of the value this cell contains */
  displayValue: string;
  /** the background color of this cell */
  backgroundColor?: string;
  /** the text color of this cell */
  textColor?: string;
  /** icon if this cell has one */
  icon?: string;
}

/** TableRows can specify their cells via "[column field]: [row value]" key-value pairs on the TableRow,
 * or via the `cells` property (see comment on `cells` property below).
 *
 * The current key-value approach in e.g. funnels isn't great because it often requires passing a
 * "sortTransform" function with the column to produce a sortable value, instead of just providing a
 * separate "label" and "value"
 */
export interface TableRow<T extends Record<string, any> = any> {
  /** Identifier for given row */
  identifier: string;
  /** Whether the row was newly added by user via a create action */
  isNew?: boolean;
  /** Whether the row should be shown selected. Applies to multi-select tables */
  isSelected?: boolean;
  /** Whether the row is selectable (will disable checkboxes) */
  isSelectDisabled?: boolean;
  /** A string to be displayed via a tooltip when selection is disabled */
  selectDisabledReason?: string;
  /** If this row is selected, override the checkmark color. */
  checkedColor?: string;
  /**
   * To specify the values for the columns in the rows, you can either have them as fields on the TableRow, or you can
   * pass them in the `cells` field. By using the cells field, you can specify different styling information for each
   * cell (e.g. background color, text color). This is currently used in Retention. When using `cells` to specify the
   * cell values, you should provide `cellRenderer` functions for the columns that reads the value from the associated
   * cell.
   */
  cells?: T[];
  /** background color for a specific line*/
  backgroundColor?: string;
  /** rows that are grouped to this row. This row is parent row */
  groupedRows?: Array<TableRow<T>>;
  /** Whether the grouped rows should be collapsed */
  isCollapsed?: boolean;
}

export interface CellRenderContext {
  field: string;
  label: string;
  isComparison: boolean;
  columnIdx: number;
  rowIdx: number;
  /**
   * Each row can have a list of grouped rows underneath it (this is used in Retention curve table, where the main row
   * is the segment and the grouped rows are for individual dates.
   */
  groupedRowIdx?: number;
  totalRows: number;
}

export interface ColumnRenderContext {
  colIndex: number;
  title: string;
}

export interface ModifierKeys {
  alt: boolean;
  ctrl: boolean;
  shift: boolean;
  meta: boolean;
}

export interface CellTooltip {
  left: number;
  top: number;
  text: string;
}

export type CellClickEvent<T = TableRow<TableCell>> = CustomEvent<{
  row: T;
  field: TableColumn['field'];
  context: CellRenderContext;
  modifierKeys: ModifierKeys;
  target: HTMLElement;
}>;

export type SelectionChangeEvent<T extends TableRow<any> = TableRow<TableCell>> = CustomEvent<{
  changed: Array<{isSelected: boolean; row: T}>;
  /** Whether this selection change was caused by the toggle-all button. */
  isToggleAll?: boolean;
  rows: T[];
}>;

export type CollapseChangeEvent<T extends TableRow<any> = TableRow<TableCell>> = CustomEvent<{
  changed: Array<{isCollapsed: boolean; row: T}>;
  /** Whether this selection change was caused by the toggle-all button. */
  isToggleAll?: boolean;
  rows: T[];
}>;

export type SortChangeEvent<T = TableRow<TableCell>> = CustomEvent<{
  sortField: TableColumn['field'];
  sortOrder: number;
  /** Whether this sort was initiated by a user action. */
  isHeaderClick: boolean;
  /** current table rows, sorted */
  rows: T[];
}>;

//#region multi-sort
export type ColSortOptionValue = {segmentIndex: number; sort: ColSortConfig};

export type ColSortConfig = {
  sortBy: SortBy.Label | SortBy.Value;
  sortOrder: SortOrder;
  // valueField required when sorting by value (sortBy.Value) and there is > 1 valueField
  valueField?: string;
};
/** sort by segment columns (segment-grouped sort) */
export type SortByColumnsConfig = {
  sortBy: SortBy.Column;
  colSortAttrs: ColSortConfig[];
};
/** sort by a single value column (flat sort). Still persists colSort info */
export type SortByValueConfig = {
  sortBy: SortBy.Value;
  sortOrder: SortOrder;
  valueField: string;
  colSortAttrs: ColSortConfig[];
};
export type TableSortConfig = SortByColumnsConfig | SortByValueConfig;

/** Summary data for each segment, e.g.: {Chrome: {sum: 12, children: {USA: {sum: 6}, India: {sum: 6}}}, Safari: ...} */
export type SegmentStats = {
  [segmentValue: string]: {
    sum: number;
    children?: SegmentStats;
  };
};
//#endregion
